<template>
  <section id="OrderHistory">
    <div id="acctOrders">
      <div class="container">
        <div id="ao-table">
          <div class="row align-items-center">
            <div class="col-lg-3 mb-4 mb-lg-0">
              <h2>Recent Quotes</h2>
            </div>
            <div class="col-lg-3 mb-4 mb-lg-0">
              <router-link to="/rfq/create" class="btn btn-primary">
                Create New Quote
              </router-link>
            </div>
            <div class="col-lg-3 mb-4 mb-lg-0">
              <router-link to="/account/savedcarts?tab=orders" class="btn btn-dark">
                Order History
              </router-link>
            </div>
          </div>

          <div class="row" v-if="!loading && noCompany">
            <div class="col-12 mt-4">
              <div class="no-quotes-notice">
                <p>Please select a company from the dropdown above to view your quote history.</p>
              </div>
            </div>
          </div>

          <div
            class="row"
            v-if="!loading && !noCompany && rfqs.length === 0 && fromOrders"
          >
            <div class="col-12 mt-4">
              <div class="no-quotes-notice">
                <h3>No quotes found either.</h3>
                <p>Redirecting back to Order History...</p>
              </div>
            </div>
          </div>

          <div
            class="row"
            v-if="!loading && !noCompany && rfqs.length === 0 && !fromOrders"
          >
            <div class="col-12 mt-4">
              <div class="no-quotes-notice">
                <h3>You have no current quotes.</h3>
              </div>
            </div>
          </div>

          <b-table
            id="rfqTable"
            stacked="lg"
            :fields="rfqsFields"
            :items="rfqs"
            :per-page="0"
            :current-page="currentPage"
            responsive="sm"
            v-if="!loading && !noCompany && rfqs.length > 0"
          >
            <template v-slot:head(dateCreated)="data">
              <p class="text-center mb-0">{{ data.label }}</p>
              <div class="ui input">
                <date-picker
                  v-model="search.fields.date"
                  format="MM/DD/YYYY"
                  :placeholder="data.label"
                  :disabled-date="disabledDate"
                ></date-picker>
              </div>
            </template>

            <template v-slot:head(id)="data">
              <p class="text-center mb-0">{{ data.label }}</p>
              <div class="ui input">
                <input
                  type="text"
                  v-model="search.fields.order"
                  :placeholder="data.label"
                />
              </div>
            </template>

            <template v-slot:head(company.code)="data">
              <p class="text-center mb-0">{{ data.label }}</p>
              <div class="ui input">
                <input
                  type="text"
                  v-model="search.fields.companyCode"
                  :placeholder="data.label"
                  readonly
                />
              </div>
            </template>

            <template v-slot:head(cost)="data">
              <p class="text-center mb-0">{{ data.label }}</p>
              <div class="ui input">
                <input
                  type="number"
                  v-model="search.fields.orderTotal"
                  :placeholder="data.label"
                />
              </div>
            </template>

            <template v-slot:head(reorder)="data">
              <p class="text-center mb-0">&nbsp;</p>
              <div class="ui input">
                <span
                  class="fa fa-search icon-button"
                  id="searchFilterRfq"
                  @click="doFilter"
                ></span>
                <b-tooltip target="searchFilterRfq" triggers="hover">
                  Search Filter
                </b-tooltip>

                <span
                  class="fa fa-refresh icon-button"
                  id="clearFilterRfq"
                  @click="clearFilter"
                ></span>
                <b-tooltip target="clearFilterRfq" triggers="hover">
                  Clear Filter
                </b-tooltip>
              </div>
            </template>

            <template v-slot:cell(dateCreated)="data">
              {{ data.item.dateCreated | formatDate }}
            </template>

            <template v-slot:cell(id)="data">
              {{ data.item.id }}
            </template>

            <template v-slot:cell(company.code)="data">
              {{ data.item.company && data.item.company.code ? data.item.company.code : '' }}
            </template>

            <template v-slot:cell(cost)="data">
              {{ data.item.cost | currency }}
            </template>

            <template v-slot:cell(reorder)="data">
              <div class="nav-item dropdown">
                <a
                  class="nav-link dropdown-toggle"
                  data-toggle="dropdown"
                  href="#"
                  role="button"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <span>Select an Option</span>
                </a>

                <div class="dropdown-menu">
                  <a
                    class="dropdown-item table-item"
                    @click="viewQuote(data.item.id)"
                  >
                    Review
                  </a>
                  <a
                    class="dropdown-item table-item"
                    @click="createOrder(data.item.id)"
                  >
                    Create Order
                  </a>
                </div>
              </div>
            </template>
          </b-table>

          <b-pagination
            v-if="!noCompany && rfqs.length > 0"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            aria-controls="rfqTable"
          ></b-pagination>

          <div class="table-notice" v-if="!noCompany && rfqs.length > 0">
            *Please note that pricing is subject to change on re-orders.
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import httpClient from "@/assets/js/HttpClient";
import FeApiService from "@/assets/js/FeApiService";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import Vue from "vue";
import Vue2Filters from "vue2-filters";
import moment from "moment";

const apiService = new FeApiService();

Vue.use(Vue2Filters);

export default {
  name: "RFQHistory",
  components: { DatePicker },
  data() {
    return {
      search: {
        filterText: "",
        filters: "",
        fields: {
          date: "",
          order: "",
          companyCode: "",
          orderTotal: "",
        },
        inactive: false,
      },
      currentPage: 1,
      perPage: 10,
      rows: 0,
      sortBy: "dateCreated|desc",
      loading: true,
      fromOrders: false,
      noCompany: false,
      rfqs: [],
      rfqsFields: [
        { key: "dateCreated", label: "Date", searchKey: "date", search: "" },
        { key: "id", label: "Quote #", searchKey: "order", search: "" },
        {
          key: "company.code",
          label: "Selected Company Code",
          searchKey: "companyCode",
          search: "",
        },
        {
          key: "cost",
          label: "Quote Total",
          searchKey: "orderTotal",
          search: "",
        },
        { key: "reorder", label: "" },
      ],
      suppressPageWatch: false,
    };
  },
  mounted() {
    this.fromOrders = this.$route.query.fromOrders === "true";

    const selectedCompanyId = parseInt(localStorage.companyId, 10);
    const selectedCompanyCode = localStorage.companyCode;

    if (!selectedCompanyId || Number.isNaN(selectedCompanyId)) {
      this.noCompany = true;
      this.loading = false;
      return;
    }

    this.noCompany = false;
    this.search.fields.companyCode = selectedCompanyCode || "";
    this.fetchData();
  },
  methods: {
    viewQuote(id) {
      this.$router.push({
        name: "RFQ Details",
        params: {
          id: id,
        },
      });
    },
    createOrder(id) {
      apiService
        .rfqOrder({
          id: id,
        })
        .then((request) => {
          this.orderSuccessful(request);
        })
        .catch((err) => this.orderFailed(err));
    },
    orderSuccessful(request) {
      localStorage.cartId = request.id;
      this.$router.push({
        name: "Cart",
        params: {
          id: request.id,
        },
      });
    },
    orderFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    disabledDate(date) {
      const maxDate = new Date();
      return date > maxDate;
    },
    clearFilter() {
      const companyCode = this.search.fields.companyCode;
      Object.keys(this.search.fields).forEach((item) => {
        this.search.fields[item] = "";
      });
      this.search.fields.companyCode = companyCode;
      this.search.filterText = "";
      this.currentPage = 1;
      this.fetchData();
    },
    doFilter() {
      let filterText = [];
      Object.keys(this.search.fields).forEach((item) => {
        if (item === "date" && this.search.fields[item]) {
          const dateFormat = moment(this.search.fields[item]).format("MM/DD/YYYY");
          filterText.push(`${item}-${dateFormat}`);
        } else if (this.search.fields[item] && item !== "companyCode") {
          filterText.push(`${item}-${this.search.fields[item]}`);
        }
      });

      this.search.filterText = filterText.join("|");
      this.currentPage = 1;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;

      const selectedCompanyId = parseInt(localStorage.companyId, 10);
      if (!selectedCompanyId || Number.isNaN(selectedCompanyId)) {
        this.noCompany = true;
        this.rfqs = [];
        this.rows = 0;
        this.loading = false;
        return;
      }

      this.noCompany = false;
      this.search.fields.companyCode = localStorage.companyCode || "";

      let url = `/cart?page=${this.currentPage}&per_page=${this.perPage}&sort=${encodeURIComponent(this.sortBy)}&cid=${selectedCompanyId}`;

      if (this.search.filterText) {
        url += `&filter=${encodeURIComponent(this.search.filterText)}`;
      }

      try {
        const res = await httpClient.get(url).then((response) => response.data);

        this.suppressPageWatch = true;
        this.rows = res.total || 0;
        this.currentPage = res.current_page || this.currentPage;
        this.perPage = res.per_page || this.perPage;
        this.rfqs = Array.from(res.data || []);
        this.loading = false;

        this.$nextTick(() => {
          this.suppressPageWatch = false;
        });

        if (this.rfqs.length === 0 && this.fromOrders) {
          setTimeout(() => {
            this.$router.replace("/account/savedcarts?tab=orders&noQuotes=true");
          }, 2000);
        }
      } catch (error) {
        console.error("RFQ fetch failed:", error);
        this.rfqs = [];
        this.rows = 0;
        this.loading = false;
      }
    },
  },
  watch: {
    currentPage(newVal, oldVal) {
      if (this.suppressPageWatch) {
        return;
      }
      if (newVal !== oldVal) {
        this.fetchData();
      }
    },
  },
};
</script>

<style scoped>
.icon-button {
  padding-top: 1rem;
  padding-left: 10px;
  cursor: pointer;
  color: #333;
}

.icon-button:hover {
  color: #c0392b;
}

/* ===== FIX DROPDOWN VISIBILITY ===== */
.nav-link.dropdown-toggle {
  color: #333 !important;
  font-weight: 600;
  font-size: 13px;
}

.nav-link.dropdown-toggle:hover {
  color: #c0392b !important;
}

/* Force caret + hidden icons to show */
.nav-link.dropdown-toggle::after {
  margin-left: 6px;
  color: #333 !important;
}

/* Dropdown menu styling */
.dropdown-menu {
  min-width: 160px;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.dropdown-item {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
  color: #c0392b;
}

/* ===== SEARCH + REFRESH ICON FIX ===== */
.fa {
  color: #333 !important;
  font-size: 14px;
}

.fa:hover {
  color: #c0392b !important;
}

/* ===== NO QUOTES MESSAGE ===== */
.no-quotes-notice {
  text-align: center;
  padding: 30px 20px;
}

.no-quotes-notice h3 {
  margin-bottom: 10px;
}

.no-quotes-notice p {
  color: #999;
  font-size: 14px;
}
</style>